import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event.target instanceof Element
        ? event.target.closest('li,slideshow-slide')
        : null;

    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      !sourceImage ||
      !targetImage
    ) {
      return open();
    }

    const itemTransitionName = 'gallery-item-open';

    sourceImage.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    const focalPoint = sourceImage.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');

      targetImage.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );
    });

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image.
   *
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (
      !mediaContainer.classList.contains(
        'product-media-container--image'
      )
    ) {
      return false;
    }

    const image = mediaContainer.querySelector(
      'img.product-media__image'
    );

    if (!image || !(image instanceof HTMLImageElement)) {
      return false;
    }

    const highResolutionUrl = image.getAttribute(
      'data_max_resolution'
    );

    if (
      !highResolutionUrl ||
      this.#highResImagesLoaded.has(highResolutionUrl)
    ) {
      return false;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Updates the active thumbnail while scrolling.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(thumbnails.querySelectorAll('button')).forEach(
      (button, i) => {
        button.setAttribute(
          'aria-selected',
          `${i === activeIndex}`
        );
      }
    );

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Clicking the photo shows the next photo.
   * Clicking the surrounding space closes the dialog.
   *
   * @param {MouseEvent} event
   */
  nextImage(event) {
    if (!(event.target instanceof Element)) return;

    const container = event.target.closest(
      '.product-media-container--image'
    );

    if (!container) return;

    const { media } = this.refs;
    const currentIndex = media.indexOf(container);

    if (currentIndex < 0) return;

    const image = container.querySelector(
      'img.product-media__image'
    );

    if (!(image instanceof HTMLImageElement)) return;
    if (!image.naturalWidth || !image.naturalHeight) return;

    // Determine the visible photo area with object-fit: contain.
    const rect = image.getBoundingClientRect();

    const scale = Math.min(
      rect.width / image.naturalWidth,
      rect.height / image.naturalHeight
    );

    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    const left = rect.left + (rect.width - width) / 2;
    const top = rect.top + (rect.height - height) / 2;

    const clickedOnImage =
      event.clientX >= left &&
      event.clientX <= left + width &&
      event.clientY >= top &&
      event.clientY <= top + height;

    if (!clickedOnImage) {
      this.close();
      return;
    }

    // Find the next photo, wrapping around after the last one.
    for (let step = 1; step < media.length; step++) {
      const nextIndex = (currentIndex + step) % media.length;

      if (
        media[nextIndex].classList.contains(
          'product-media-container--image'
        )
      ) {
        this.selectThumbnail(nextIndex, {
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        });

        return;
      }
    }
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      return this.closeDialog();
    }

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = 'gallery-item-close';

    const mediaGallery =
      /** @type {import('./media-gallery').MediaGallery | undefined} */ (
        this.closest('media-gallery')
      );

    const slideshowActive =
      mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[activeIndex]
      : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;

    if (focalPoint) {
      document.documentElement.style.setProperty(
        '--gallery-media-focal-point',
        focalPoint
      );
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty(
      'view-transition-name',
      itemTransitionName
    );

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty(
        'view-transition-name'
      );

      slide.style.setProperty(
        'view-transition-name',
        itemTransitionName
      );

      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');

    document.documentElement.style.removeProperty(
      '--gallery-media-focal-point'
    );
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog with Escape.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion()
      ? 'instant'
      : 'smooth';

    this.selectThumbnail(index, { behavior });
  }

  /**
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;

    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Selects a thumbnail and scrolls to its media.
   *
   * @param {number} index
   * @param {Object} options
   * @param {ScrollBehavior} options.behavior
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (
      !this.refs.thumbnails ||
      !this.refs.thumbnails.children.length
    ) {
      return;
    }

    if (
      isNaN(index) ||
      index < 0 ||
      index >= this.refs.thumbnails.children.length
    ) {
      return;
    }

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (
      !targetThumbnail ||
      !(targetThumbnail instanceof HTMLElement)
    ) {
      return;
    }

    Array.from(thumbnails.querySelectorAll('button')).forEach(
      (button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      }
    );

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Gets the most visible element.
 *
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement>}
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio
            ? current
            : prev
        );

        observer.disconnect();

        resolve(
          /** @type {HTMLElement} */ (mostVisible.target)
        );
      },
      {
        threshold: Array.from(
          { length: 100 },
          (_, i) => i / 100
        ),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}